import * as React from "react";

import {
  AppBar,
  Box,
  Divider,
  makeStyles,
  Tab,
  Tabs,
  Theme,
  Typography,
} from "@material-ui/core";

import { MARQETAJS_URL } from "client/config";
import { OnboardingStatusContext } from "client/context";
import {
  OnboardingStatusToCurrentStateMap,
  Status,
  StatusLabel,
  StatusMap,
  StatusStep,
  useStyles,
} from "client/domain/OnboardingStatus";
import {
  useBranding,
  useLocalStorageState,
  useMerchantModel,
  useOnboardingStatusContext,
  useRunOnce,
  useScript,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";

import {
  SuccessIcon,
  SuccessPanel,
  TabPanel,
} from "./SelfServeActivation.components";
import { SelfServeActivationTabPanel } from "./SelfServeActivationTabPanel";

const onboardingStatusToCurrentStateMap: OnboardingStatusToCurrentStateMap = {
  INELIGIBLE: "None",
  NO_RECORD: "None",
  IN_PROGRESS: "None",
  IN_REVIEW: "InProgress",
  REQUIRES_ATTENTION: "None",
  READY_TO_INSTALL: "InProgress",
  COMPLETE: "InProgress",
};

type CurrentStateParams = Pick<OnboardingStatusContext, "onboardingStatus"> & {
  allDummyTapsComplete: boolean;
};

export const determineCurrentState: (params: CurrentStateParams) => Status = ({
  onboardingStatus,
  allDummyTapsComplete,
}) => {
  if (allDummyTapsComplete) {
    return "Done";
  }

  return onboardingStatusToCurrentStateMap[onboardingStatus];
};

function a11yProps(index: number) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

const useActivationStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  fullWidthDivider: {
    margin: theme.spacing(0, -3),
  },
  label: {
    fontWeight: "bold",
    textTransform: "none",
  },
}));

type Props = {
  currentState: Status;
  cardTapSuccessStates: boolean[];
  setCardTapSuccessStates: React.Dispatch<React.SetStateAction<boolean[]>>;
};

const InProgressSubStep: React.FC<Props> = ({
  currentState,
  cardTapSuccessStates,
  setCardTapSuccessStates,
}) => {
  const { merchant } = useMerchantModel();
  const activationClasses = useActivationStyles();

  const updateCardTapSuccess = (index: number) => {
    setCardTapSuccessStates((prevState) => {
      const newState = [...prevState];
      newState[index] = true;
      return newState;
    });
  };

  //  Load marqeta js sdk
  const marqetaScriptStatus = useScript(MARQETAJS_URL);
  const [activeLocationIndex, setActiveLocationIndex] = React.useState<number>(
    0
  );
  const handleStoreLocationTabChange = (_: unknown, newValue: number) => {
    setActiveLocationIndex(newValue);
  };

  const continueToNextStore = () => {
    // find the next store that isn't activated
    const nextTabIdx = cardTapSuccessStates.findIndex(
      (v, idx) => activeLocationIndex !== idx && !v
    );
    if (nextTabIdx !== undefined) {
      setActiveLocationIndex(nextTabIdx);
    }
  };

  if (currentState === "InProgress") {
    return (
      <Box className={activationClasses.root} mt={1}>
        <AppBar elevation={0} position="static">
          <Tabs
            value={activeLocationIndex}
            onChange={handleStoreLocationTabChange}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable store location tabs"
          >
            {merchant.locations.map((location, index) => (
              <Tab
                className={activationClasses.label}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                key={`Tab${index}`}
                label={
                  <Box display="flex" alignItems="center" gridGap={12}>
                    <Typography>{location.locationName.value}</Typography>
                    {Boolean(cardTapSuccessStates[index]) ? (
                      <SuccessIcon size="small" />
                    ) : null}
                  </Box>
                }
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
          <Divider
            className={activationClasses.fullWidthDivider}
            color="default"
          />
        </AppBar>
        {/* Location will be used when we we are ready to call getClientAccessToken */}
        {merchant.locations.map((_location, index) => (
          <TabPanel
            index={index}
            key={`TabPanel${index}`}
            value={activeLocationIndex}
          >
            {Boolean(cardTapSuccessStates[index]) ? (
              <SuccessPanel continueToNextStore={continueToNextStore} />
            ) : (
              <SelfServeActivationTabPanel
                setCardTapSuccessState={() => updateCardTapSuccess(index)}
                key={`TabPanel${index}`}
                marqetaScriptStatus={marqetaScriptStatus}
                activeLocationIndex={activeLocationIndex}
              />
            )}
          </TabPanel>
        ))}
      </Box>
    );
  }
  return null;
};

export const SelfServeActivationStep: React.FC = () => {
  const { onboardingStatus } = useOnboardingStatusContext();

  const { brandTitle } = useBranding();
  const { merchant } = useMerchantModel();
  const [cardTapSuccessStates, setCardTapSuccessStates] = React.useState(
    merchant.locations.map((_) => false)
  );

  // when all stores have been activated, persist to local storage so when the merchant refreshes we don't have to make multiple fetch calls to re-check
  const [allDummyTapsComplete, setAllDummyTapsComplete] = useLocalStorageState(
    "allTapsComplete",
    cardTapSuccessStates.filter(Boolean).length === merchant.locations.length
  );

  React.useEffect(() => {
    if (!allDummyTapsComplete) {
      setAllDummyTapsComplete(
        cardTapSuccessStates.filter(Boolean).length ===
          merchant.locations.length
      );

      logAmplitudeEvent(
        "SELF_SERVE_INSTORE",
        "succesfully_activated_all_terminals"
      );
    }
  }, [
    allDummyTapsComplete,
    cardTapSuccessStates,
    merchant.locations.length,
    setAllDummyTapsComplete,
  ]);

  const rootClasses = useStyles();

  const currentState = determineCurrentState({
    onboardingStatus,
    allDummyTapsComplete,
  });

  const statusMap: StatusMap = {
    None: {},
    InProgress: {
      className: rootClasses.light,
    },
    Done: {
      className: rootClasses.dark,
    },
    Paused: {},
    Reviewing: {
      className: rootClasses.light,
    },
    RequiresAttention: {},
    Declined: {},
  };

  const { className } = statusMap[currentState];
  const currentTapsComplete = cardTapSuccessStates.filter(Boolean).length;
  const totalTapsComplete = merchant.locations.length;

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_PAGE", "self_serve_activation_step_loaded")
  );

  return (
    <StatusStep
      timeToFade={500}
      activeOnboardingStep="ActivateInStore"
      stepStatus={currentState}
    >
      <Box
        data-testid="self-serve-activation"
        className={`${className} ${rootClasses.statusWrapper}`}
        mb={2}
        p={2}
      >
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>Activate {brandTitle} Card in-store</strong>
          </Typography>
          <StatusLabel status={currentState} />
          {!allDummyTapsComplete ? (
            <Typography className={rootClasses.cardTapCounter} variant="body2">
              {currentTapsComplete} of {totalTapsComplete} stores activated
            </Typography>
          ) : null}
        </Box>
        <InProgressSubStep
          setCardTapSuccessStates={setCardTapSuccessStates}
          cardTapSuccessStates={cardTapSuccessStates}
          currentState={currentState}
        />
      </Box>
    </StatusStep>
  );
};
